import React, { ComponentProps, PropsWithChildren } from "react";
import { Mail } from "@tamagui/lucide-icons";
import {
  Anchor,
  styled,
  Text,
  Image as TGImage,
  Separator as TGSeparator,
  View,
} from "tamagui";

import { Download } from "../../../icons/download";
import { Button, ButtonBaseProps } from "../../ui/button";
import { Card } from "../../ui/card";

const ScoreWrapper = styled(View, {
  name: "ScoreWrapper",
  borderRadius: "$4.5",
  paddingHorizontal: "$4",
  paddingVertical: "$2",
  variants: {
    state: {
      success: {
        backgroundColor: "$socore-10-9-o005",
      },
      warning: {
        backgroundColor: "$warning-500-o005",
      },
      neutral: {
        backgroundColor: "$gray-600-o005",
      },
      danger: {
        backgroundColor: "$error-500-o005",
      },
    },
  } as const,
  defaultVariants: {
    state: "neutral",
  },
});

const ScoreText = styled(Text, {
  name: "ScoreText",
  fontFamily: "$subtitle",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      lg: {
        fontWeight: "$5",
        fontSize: "$9",
        lineHeight: "$9",
      },

      md: {
        fontWeight: "$5",
        fontSize: "$7",
        lineHeight: "$7",
      },
    },
  } as const,
  defaultVariants: {
    state: "neutral",
    font: "lg",
  },
});

const ScoreContainer = styled(Text, {
  name: "ScoreContainer",
  fontFamily: "$subtitle",
  color: "$gray-100",
  variants: {
    font: {
      lg: {
        fontWeight: "$4", //TODO: add font 300
        fontSize: "$9",
        lineHeight: "$9",
      },

      md: {
        fontWeight: "$5",
        fontSize: "$7",
        lineHeight: "$7",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const ScoreTitle = styled(Text, {
  name: "ScoreTitle",
  fontFamily: "$body",
  variants: {
    variant: {
      default: {
        color: "$gray-950",
      },
    },
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
    variant: "default",
  },
});

const DynamicDescription = styled(Text, {
  name: "DynamicDescription",
  fontFamily: "$subtitle",
  variants: {
    variant: {
      default: {
        color: "$gray-primary-900",
      },
    },
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$4",
        lineHeight: "$4",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    font: "lg",
  },
});

const DynamicText = styled(Text, {
  name: "DynamicText",
  fontFamily: "$body",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      lg: {
        fontWeight: "$4",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
});

type DynamicsState = ComponentProps<typeof DynamicText>["state"];

const Dynamics: React.FC<{
  state: DynamicsState;
  description: string;
  text?: string;
}> = ({ description, text, state }) => {
  return (
    <View justifyContent="center" alignItems="center" gap="$0.5">
      <DynamicDescription data-id="dynamic-description">
        {description}
      </DynamicDescription>
      {text && (
        <DynamicText textAlign="center" data-id="dynamic-text" state={state}>
          {text}
        </DynamicText>
      )}
    </View>
  );
};

type ScoreState = ComponentProps<typeof ScoreText>["state"];

type ScoreFont = ComponentProps<typeof ScoreText>["font"];

const Score: React.FC<
  PropsWithChildren & { max?: number; state: ScoreState; font?: ScoreFont }
> = ({ state, max = 100, font = "lg", children }) => {
  return (
    <ScoreContainer data-id="score-container" font={font}>
      <ScoreText data-id="score-value" state={state} font={font}>
        {children}
      </ScoreText>
      /{max}
    </ScoreContainer>
  );
};

const Separator = styled(TGSeparator, {
  name: "Separator",
  width: "100%",
  borderColor: "$gray-200",
});

const DownloadBtn: React.FC<
  PropsWithChildren & { href: string } & ButtonBaseProps
> = ({ children, href, ...props }) => {
  return (
    <Anchor href={href} textDecorationLine="none" asChild>
      <Button
        alignItems="center"
        gap={"$0.5"}
        icon={<Download style={{ flexShrink: 0 }} />}
        variant="outlined"
        size="md"
        rounded="lg"
        width="100%"
        {...props}
      >
        {children}
      </Button>
    </Anchor>
  );
};

const SendMailBtn: React.FC<PropsWithChildren & ButtonBaseProps> = ({
  children,
  ...props
}) => {
  return (
    <Button
      alignItems="center"
      gap={"$1"}
      icon={<Mail width={20} height={20} />}
      variant="outlined"
      size="md"
      rounded="lg"
      {...props}
    >
      {children}
    </Button>
  );
};

type CardProps = ComponentProps<typeof Card>;

const Container: React.FC<CardProps> = (props) => {
  return (
    <Card
      flexShrink={1}
      alignSelf={"stretch"}
      shadow="md"
      alignItems="center"
      paddingBottom={"$4"}
      paddingHorizontal={"$4"}
      maxWidth={344}
      borderRadius={"$9"}
      {...props}
    />
  );
};

type ImageProps = ComponentProps<typeof TGImage> & { path?: string };

const Image: React.FC<ImageProps> = ({ path, source, ...props }) => {
  return (
    <TGImage
      style={{ width: 120, height: 120 }}
      source={path ? { width: 120, height: 120, uri: path } : source}
      {...props}
    />
  );
};

const DownloadContainer = styled(View, {
  name: "DownloadContainer",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "$2",
  backgroundColor: "$gray-50",
  borderRadius: "$9",
  padding: "$2",
});

export const ComposedTextScoreCard = {
  Container,
  Score,
  ScoreTitle,
  Dynamics,
  Separator,
  DownloadContainer,
  DownloadBtn,
  SendMailBtn,
  Image,
  ScoreWrapper,
};
